/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ButtonIconPresentationProps, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@monsma/XDL/Helpers";
import { css } from "styled-components";

interface SharedListPagesHeader {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    toggleFilterGridItem?: GridItemProps;
    toggleFilterClickable?: ClickablePresentationProps;
    toggleFilterIcon?: IconPresentationProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    headingText?: TypographyPresentationProps;
}

export const sharedListPagesHeaderStyle: SharedListPagesHeader = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 10px;
        `,
    },
    countGridItem: {
        width: 6,
        css: css`
            order: 1;
            align-items: center;
            justify-content: flex-end;
            font-weight: bold;
            > div {
                width: auto;
            }
            .total-label {
                font-weight: normal;
            }
        `,
    },
    toggleFilterGridItem: {
        width: 6,
        css: css`
            order: 0;
        `,
    },
    toggleFilterButton: {
        css: css`
            && {
                padding: 10px 15px;
                border: 1px solid ${getColor("common.ui12")};
                border-radius: 0;
                background: ${getColor("common.background")};
                min-width: 100px;
                margin: 0;
                justify-content: flex-start;
                &:hover,
                &:active,
                &:focus {
                    border-color: ${getColor("common.ui12")};
                    background: ${getColor("common.background")};
                }
            }
        `,
    },
    toggleFilterButtonIcon: {
        src: Filter,
        size: 15,
        css: css`
            margin-right: 5px;
                color: ${getColor("text.main")} !important;
        `,
    },
    headingText: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
};

interface SharedListPagesTabel {
    container?: InjectableCss;
    noResultsContainer?: InjectableCss;
}

export const sharedListPagesTabelStyle: SharedListPagesTabel = {
    container: {
        css: css`
            overflow: auto;
            margin-top: 20px;
        `,
    },
    noResultsContainer: {
        css: css`
            margin-top: 20px;
        `,
    },
};

interface SharedListPagesFilter {
    container?: GridContainerProps;
    normalGridItem?: GridItemProps;
    dateGridItem?: GridItemProps;
    buttonsItem?: GridItemProps;
    customContainer?: GridContainerProps;
    clearFiltersButton?: ButtonProps;
}

export const sharedListPagesFilterStyle: SharedListPagesFilter = {
    container: {
        gap: 20,
        css: css`
            padding: 15px;
            background: ${getColor("common.background")};
            border: 1px solid ${getColor("common.ui12")};
            margin-top: -11px;
        `,
    },
    normalGridItem: {
        width: [12, 12, 6, 4, 4],
    },
    dateGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            > div {
                width: 100%;
                display: flex;
            }
        `,
    },
    customContainer: {
        gap: 20,
    },
    buttonsItem: {
        width: 12,
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
            ${screen("lte", "xs")} {
                && {
                    width: 100%;
                }
            }
        `,
    },
};
